/**
 * Product-line scorecard.
 *
 * Columns lead with PER-REP normalized figures. Team sizes here are wildly uneven
 * (29/24/21/17/6/2/2/1 active members), so ranking on totals would just rank headcount
 * and tell the owner nothing about which line actually performs.
 *
 * Contracts are attributed by product line (contract_types.company + category), not by
 * team membership: 11 of 90 workers sit on 2-3 teams, so a membership rollup would
 * double-count their revenue. Verified against production - product-line revenue sums
 * exactly to the company total.
 */

import { useMemo, useState } from "react";
import { TableCell, TableRow } from "flowbite-react";
import { SortableTable } from "../SortableTable";
import type { SortState } from "../../lib/sorting";
import { updateSortState } from "../../lib/sorting";
import type { TeamMetrics } from "../../services/sales-metrics";
import { formatCurrency, formatNumber, formatRate } from "./format";

type SortKey =
  | "productLine"
  | "revenuePerRep"
  | "revenue"
  | "contractsSigned"
  | "sellingReps"
  | "avgContractValue"
  | "cancelRate";

const COLUMNS: { key: SortKey; label: string; sortable?: boolean; explanation?: string }[] = [
  {
    key: "productLine",
    label: "Produktlinie",
    sortable: true,
    explanation:
      "Name der Produktsparte (z. B. Strom, Gas, Glasfaser), zugeordnet anhand der Vertragsart.",
  },
  {
    key: "revenuePerRep",
    label: "Umsatz / Vertriebler",
    sortable: true,
    explanation:
      "Gesamter erzielter Umsatz der Produktlinie geteilt durch aktive Vertriebler mit Abschluss. Ermöglicht fairen Leistungsvergleich unabhängig von der Teamgröße.",
  },
  {
    key: "revenue",
    label: "Umsatz gesamt",
    sortable: true,
    explanation:
      "Summe des Provisions- und Vertragswerts aller unterschriebenen Verträge in dieser Produktlinie im Zeitraum.",
  },
  {
    key: "contractsSigned",
    label: "Verträge",
    sortable: true,
    explanation: "Gesamtzahl der erfolgreich unterzeichneten Verträge in dieser Produktlinie.",
  },
  {
    key: "sellingReps",
    label: "Vertriebler",
    sortable: true,
    explanation:
      "Anzahl der Vertriebler, die im gewählten Zeitraum mindestens einen Vertrag in dieser Produktlinie abgeschlossen haben.",
  },
  {
    key: "avgContractValue",
    label: "Ø Vertragswert",
    sortable: true,
    explanation:
      "Durchschnittlicher Provisionswert pro unterzeichnetem Vertrag in dieser Sparte (Umsatz gesamt / Verträge).",
  },
  {
    key: "cancelRate",
    label: "Stornoquote",
    sortable: true,
    explanation:
      "Anteil der stornierten Verträge bezogen auf alle Verträge dieser Produktlinie (Storniert / (Unterschrieben + Storniert)).",
  },
];

export function TeamScorecard({ teams }: { teams: TeamMetrics[] }) {
  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: "revenuePerRep",
    direction: "desc",
  });

  const sorted = useMemo(() => {
    const dir = sortState.direction === "asc" ? 1 : -1;
    return [...teams].sort((a, b) => {
      const av = a[sortState.key];
      const bv = b[sortState.key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv) * dir;
      }
      return ((av as number) - (bv as number)) * dir;
    });
  }, [teams, sortState]);

  if (teams.length === 0) {
    return (
      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
        Keine Umsätze im gewählten Zeitraum.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="viz-scroll-x">
        <SortableTable
          columns={COLUMNS}
          sortState={sortState}
          onSort={(key) => setSortState((s) => updateSortState(s, key as SortKey))}
          flush
        >
            {sorted.map((team) => (
              <TableRow key={team.productLine}>
                <TableCell className="whitespace-nowrap p-3 text-sm font-medium">
                  {team.productLine}
                </TableCell>
                {/* Per-rep first: it is the comparable figure. */}
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm font-medium">
                  {team.revenuePerRep === null ? "—" : formatCurrency(team.revenuePerRep)}
                </TableCell>
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(team.revenue)}
                </TableCell>
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                  {formatNumber(team.contractsSigned)}
                </TableCell>
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                  {formatNumber(team.sellingReps)}
                </TableCell>
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                  {formatCurrency(team.avgContractValue)}
                </TableCell>
                <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                  {formatRate(team.cancelRate)}
                </TableCell>
              </TableRow>
            ))}
        </SortableTable>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Verträge werden der Produktlinie zugeordnet, nicht der Teamzugehörigkeit –
        Vertriebler in mehreren Teams würden sonst doppelt gezählt.
      </p>
    </div>
  );
}
